import { useState, useEffect } from 'react';
import {
  Search,
  Users,
  Contact,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  Mail,
  Phone,
  Eye,
  Send,
  GraduationCap,
  UserPlus,
  Plus,
  Trash2,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getAdminReferrals,
  sendAdminReminders,
  getAdminEnrollments,
  markAdminEnrollmentsSeen,
  getAdminTutors,
  getAdminCoursesForAssign,
  getAdminProgramsForAssign,
  inviteTutor,
  deleteTutorInvite,
  removeTutor,
  updateTutorAssignments,
  type AdminReferral,
  type AdminEnrollment,
  type AdminTutor,
  type AdminPendingInvite,
  type AdminCourseForAssign,
  type AdminProgramForAssign,
} from '@/lib/api';
import { GlobalLoaderPage } from '@/components/GlobalLoader';
import { SlideOver } from '@/components/SlideOver';
import { TableEmptyState } from '@/components/TableEmptyState';
import { AdminTableSkeleton } from '@/components/skeletons/AdminTableSkeleton';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const PAGE_SIZE = 10;
const CONTACT_PAGE_SIZE = 10;

const ACCEPT_CONFIG = {
  accepted: {
    label: 'Accepted',
    icon: CheckCircle,
    cls: 'bg-africa-green/15 text-africa-green border-africa-green/30',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    cls: 'bg-africa-yellow/15 text-africa-yellow border-africa-yellow/30',
  },
} as const;

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const STUDENTS_PAGE_SIZE = 10;

export function Admin() {
  const [referrals, setReferrals] = useState<AdminReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'referrals' | 'contacts' | 'students' | 'tutors'>('referrals');
  const [search, setSearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [contactFilter, setContactFilter] = useState<'all' | 'accepted' | 'pending'>('all');
  const [page, setPage] = useState(0);
  const [contactPage, setContactPage] = useState(0);
  const [selectedReferral, setSelectedReferral] = useState<AdminReferral | null>(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [selectedReferralIds, setSelectedReferralIds] = useState<Set<string>>(new Set());

  // Students (enrollments)
  const [enrollments, setEnrollments] = useState<AdminEnrollment[]>([]);
  const [newEnrollmentCount, setNewEnrollmentCount] = useState(0);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentCourseFilter, setStudentCourseFilter] = useState('');
  const [studentProgramFilter, setStudentProgramFilter] = useState('');
  const [studentsPage, setStudentsPage] = useState(0);
  const [selectedEnrollment, setSelectedEnrollment] = useState<AdminEnrollment | null>(null);

  // Tutors
  const [tutors, setTutors] = useState<AdminTutor[]>([]);
  const [pendingInvites, setPendingInvites] = useState<AdminPendingInvite[]>([]);
  const [tutorsLoading, setTutorsLoading] = useState(false);
  const [tutorInviteOpen, setTutorInviteOpen] = useState(false);
  const [tutorInviteEmail, setTutorInviteEmail] = useState('');
  const [tutorInvitePhone, setTutorInvitePhone] = useState('');
  const [tutorInviteCourseId, setTutorInviteCourseId] = useState('');
  const [tutorInviteFullAccess, setTutorInviteFullAccess] = useState(true);
  const [coursesForAssign, setCoursesForAssign] = useState<AdminCourseForAssign[]>([]);
  const [programsListForAssign, setProgramsListForAssign] = useState<AdminProgramForAssign[]>([]);
  const [tutorInviteCourseSearch, setTutorInviteCourseSearch] = useState('');
  const [tutorInviteProgramFilter, setTutorInviteProgramFilter] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [deletingInviteId, setDeletingInviteId] = useState<string | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<AdminPendingInvite | null>(null);
  const [tutorInviteEmailError, setTutorInviteEmailError] = useState('');
  const [selectedTutor, setSelectedTutor] = useState<AdminTutor | null>(null);
  const [tutorEditAssignments, setTutorEditAssignments] = useState(false);
  const [tutorAssignmentsDraft, setTutorAssignmentsDraft] = useState<string[]>([]);
  const [tutorProgramAssignmentsDraft, setTutorProgramAssignmentsDraft] = useState<string[]>([]);
  const [tutorAssignmentsSaving, setTutorAssignmentsSaving] = useState(false);
  const [tutorToRemove, setTutorToRemove] = useState<AdminTutor | null>(null);
  const [tutorRemoving, setTutorRemoving] = useState(false);

  useEffect(() => {
    getAdminReferrals()
      .then((data) => setReferrals(data.referrals))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  // Fetch enrollment count for red dot; when on Students tab load list and mark as seen
  useEffect(() => {
    if (activeTab !== 'students') {
      getAdminEnrollments()
        .then((data) => {
          setNewEnrollmentCount(data.newCount);
          setEnrollments(data.enrollments);
        })
        .catch(() => {});
      return;
    }
    setStudentsLoading(true);
    getAdminEnrollments()
      .then((data) => {
        setEnrollments(data.enrollments);
        setNewEnrollmentCount(data.newCount);
      })
      .catch(() => {})
      .finally(() => setStudentsLoading(false));
  }, [activeTab]);

  // When user opens Students tab, mark all as seen (clear red dot) and refetch so rows no longer show "new"
  useEffect(() => {
    if (activeTab !== 'students') return;
    markAdminEnrollmentsSeen()
      .then(() => {
        setNewEnrollmentCount(0);
        return getAdminEnrollments();
      })
      .then((data) => setEnrollments(data.enrollments))
      .catch(() => {});
  }, [activeTab]);

  // Fetch tutors when on Tutors tab
  useEffect(() => {
    if (activeTab !== 'tutors') return;
    setTutorsLoading(true);
    getAdminTutors()
      .then((data) => {
        setTutors(data.tutors);
        setPendingInvites(data.pendingInvites);
      })
      .catch(() => {})
      .finally(() => setTutorsLoading(false));
  }, [activeTab]);

  // Fetch courses and programs for assign when opening invite slideover or tutor detail modal
  useEffect(() => {
    if (!tutorInviteOpen && !selectedTutor) return;
    Promise.all([getAdminCoursesForAssign(), getAdminProgramsForAssign()])
      .then(([coursesData, programsData]) => {
        setCoursesForAssign(coursesData.courses ?? []);
        setProgramsListForAssign(programsData.programs ?? []);
      })
      .catch(() => {
        setCoursesForAssign([]);
        setProgramsListForAssign([]);
      });
  }, [tutorInviteOpen, selectedTutor]);

  const filteredReferrals = referrals.filter((r) => {
    const q = search.toLowerCase();
    return (
      r.referrerName.toLowerCase().includes(q) ||
      r.referrerEmail.toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q)
    );
  });

  const totalRefPages = Math.max(1, Math.ceil(filteredReferrals.length / PAGE_SIZE));
  const paginatedReferrals = filteredReferrals.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  );

  const allContacts = referrals.flatMap((r) =>
    r.contacts.map((c) => ({
      ...c,
      referrerId: r.id,
      referrerName: r.referrerName,
      referralCreatedAt: r.createdAt,
    }))
  );

  const filteredContacts = allContacts.filter((c) => {
    const q = contactSearch.toLowerCase();
    const matchSearch =
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.referrerName.toLowerCase().includes(q);
    const matchFilter =
      contactFilter === 'all' || c.accepted === contactFilter;
    return matchSearch && matchFilter;
  });

  const contactTotalPages = Math.max(
    1,
    Math.ceil(filteredContacts.length / CONTACT_PAGE_SIZE)
  );
  const paginatedContacts = filteredContacts.slice(
    contactPage * CONTACT_PAGE_SIZE,
    (contactPage + 1) * CONTACT_PAGE_SIZE
  );

  const filteredEnrollments = enrollments.filter((e) => {
    const q = studentSearch.toLowerCase().trim();
    const matchSearch = !q || (
      e.learnerName.toLowerCase().includes(q) ||
      e.learnerEmail.toLowerCase().includes(q) ||
      (e.learnerPhone && e.learnerPhone.toLowerCase().includes(q)) ||
      e.courseTitle.toLowerCase().includes(q) ||
      e.programTitle.toLowerCase().includes(q)
    );
    const matchCourse = !studentCourseFilter || e.courseId === studentCourseFilter || e.courseTitle === studentCourseFilter;
    const matchProgram = !studentProgramFilter || e.programSlug === studentProgramFilter || e.programTitle === studentProgramFilter;
    return matchSearch && matchCourse && matchProgram;
  });
  const uniqueStudentCount = new Set(filteredEnrollments.map((e) => e.learnerId)).size;
  const enrollmentsByLearner = filteredEnrollments.reduce((acc, e) => {
    const list = acc.get(e.learnerId) ?? [];
    list.push(e);
    acc.set(e.learnerId, list);
    return acc;
  }, new Map<string, AdminEnrollment[]>());
  const studentsTotalPages = Math.max(1, Math.ceil(filteredEnrollments.length / STUDENTS_PAGE_SIZE));
  const paginatedEnrollments = filteredEnrollments.slice(
    studentsPage * STUDENTS_PAGE_SIZE,
    (studentsPage + 1) * STUDENTS_PAGE_SIZE
  );
  const uniqueCourses = Array.from(
    new Map(enrollments.map((e) => [e.courseId, { courseId: e.courseId, courseTitle: e.courseTitle }])).values()
  ).filter((c) => c.courseTitle).sort((a, b) => a.courseTitle.localeCompare(b.courseTitle));
  const uniquePrograms = Array.from(new Set(enrollments.map((e) => ({ slug: e.programSlug, title: e.programTitle })))).filter((p) => p.slug).sort((a, b) => a.title.localeCompare(b.title));

  // Tutor assign: filter courses by search and program
  const programsForAssign = Array.from(
    new Map(coursesForAssign.map((c) => [c.programId, { programId: c.programId, programSlug: c.programSlug, programTitle: c.programTitle }])).values()
  ).filter((p) => p.programTitle).sort((a, b) => a.programTitle.localeCompare(b.programTitle));
  const assignCourseSearch = tutorInviteCourseSearch.toLowerCase().trim();
  const filteredCoursesForAssign = coursesForAssign.filter((c) => {
    const matchProgram = !tutorInviteProgramFilter || c.programId === tutorInviteProgramFilter || c.programSlug === tutorInviteProgramFilter;
    const matchSearch = !assignCourseSearch || (
      c.courseTitle.toLowerCase().includes(assignCourseSearch) ||
      (c.programTitle && c.programTitle.toLowerCase().includes(assignCourseSearch)) ||
      c.courseSlug.toLowerCase().includes(assignCourseSearch) ||
      (c.programSlug && c.programSlug.toLowerCase().includes(assignCourseSearch))
    );
    return matchProgram && matchSearch;
  });

  if (loading) {
    return <GlobalLoaderPage />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Tab switcher: horizontal scroll on small screens, invisible scrollbar */}
        <div className="w-full overflow-x-auto scrollbar-hide -mx-1 px-1">
          <div className="flex gap-1 bg-card border border-border rounded-xl p-1 w-max min-w-0">
            <button
              type="button"
              onClick={() => {
                setActiveTab('referrals');
                setPage(0);
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'referrals'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <Users className="w-4 h-4" />
              Referrals
              <span className="ml-1 text-xs opacity-80">({referrals.length})</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('contacts');
                setContactPage(0);
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'contacts'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <Contact className="w-4 h-4" />
              All Contacts
              <span className="ml-1 text-xs opacity-80">({allContacts.length})</span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('students'); setStudentsPage(0); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors relative ${
                activeTab === 'students'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <GraduationCap className="w-4 h-4" />
              Students
              <span className="ml-1 text-xs opacity-80">({uniqueStudentCount})</span>
              {newEnrollmentCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-destructive" aria-label={`${newEnrollmentCount} new`} />
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('tutors')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'tutors'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              Tutors
              <span className="ml-1 text-xs opacity-80">({tutors.length})</span>
            </button>
          </div>
        </div>

        {activeTab === 'referrals' && (
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-5 border-b border-border">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or ID…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  className="pl-9"
                />
              </div>
              {selectedReferralIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{selectedReferralIds.size} selected</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={sendingReminders}
                    onClick={async () => {
                      setSendingReminders(true);
                      try {
                        const result = await sendAdminReminders(2);
                        toast.success(`Reminder emails sent: ${result.sent} of ${result.attempted} referrers.`);
                        getAdminReferrals().then((data) => setReferrals(data.referrals)).catch(() => {});
                        setSelectedReferralIds(new Set());
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'Failed to send reminders');
                      } finally {
                        setSendingReminders(false);
                      }
                    }}
                    className="gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {sendingReminders ? 'Sending…' : 'Send reminder emails'}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedReferralIds(new Set())}>
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {loading ? (
              <AdminTableSkeleton rows={6} cols={6} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary/50 border-b border-border">
                      <th className="w-10 px-2 py-3">
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={paginatedReferrals.length > 0 && paginatedReferrals.every((r) => selectedReferralIds.has(r.id))}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedReferralIds(new Set(paginatedReferrals.map((r) => r.id)));
                            else setSelectedReferralIds(new Set());
                          }}
                          className="rounded border-border"
                        />
                      </th>
                      <th className="text-left font-semibold px-4 py-3 text-sm">ID</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm">Referrer</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm hidden md:table-cell">Email</th>
                      <th className="text-center font-semibold px-4 py-3 text-sm">Contacts</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm hidden sm:table-cell">Date</th>
                      <th className="text-right font-semibold px-4 py-3 text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReferrals.length === 0 ? (
                      <TableEmptyState message="No referrals found" />
                    ) : (
                      paginatedReferrals.map((ref) => (
                        <tr key={ref.id} className="border-b border-border hover:bg-secondary/30 transition-colors">
                          <td className="w-10 px-2 py-3">
                            <input
                              type="checkbox"
                              aria-label={`Select ${ref.referrerName}`}
                              checked={selectedReferralIds.has(ref.id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedReferralIds((s) => new Set(s).add(ref.id));
                                else setSelectedReferralIds((s) => { const n = new Set(s); n.delete(ref.id); return n; });
                              }}
                              className="rounded border-border"
                            />
                          </td>
                          <td className="font-mono text-xs text-muted-foreground px-4 py-3">{ref.id.slice(0, 8)}…</td>
                          <td className="font-medium px-4 py-3">{ref.referrerName}</td>
                          <td className="hidden md:table-cell text-sm text-muted-foreground px-4 py-3">{ref.referrerEmail}</td>
                          <td className="text-center px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/20 text-accent text-xs font-medium">
                              <Users className="w-3 h-3" />
                              {ref.contacts.length}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell text-sm text-muted-foreground px-4 py-3">{formatDate(ref.createdAt)}</td>
                          <td className="text-right px-4 py-3">
                            <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => setSelectedReferral(ref)}>
                              <Eye className="w-4 h-4" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {totalRefPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Showing {page * PAGE_SIZE + 1}–
                  {Math.min((page + 1) * PAGE_SIZE, filteredReferrals.length)} of{' '}
                  {filteredReferrals.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: totalRefPages }).map((_, i) => (
                    <Button
                      key={i}
                      variant={page === i ? 'default' : 'ghost'}
                      size="icon"
                      className="h-8 w-8 text-xs"
                      onClick={() => setPage(i)}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page >= totalRefPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-5 border-b border-border">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search contacts or referrer…"
                  value={contactSearch}
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    setContactPage(0);
                  }}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={contactFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setContactFilter('all');
                    setContactPage(0);
                  }}
                >
                  All
                </Button>
                <Button
                  variant={contactFilter === 'accepted' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setContactFilter('accepted');
                    setContactPage(0);
                  }}
                >
                  Accepted
                </Button>
                <Button
                  variant={contactFilter === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setContactFilter('pending');
                    setContactPage(0);
                  }}
                >
                  Pending
                </Button>
              </div>
            </div>

            {loading ? (
              <AdminTableSkeleton rows={6} cols={6} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary/50 border-b border-border">
                      <th className="text-left font-semibold px-4 py-3 text-sm">Name</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm hidden md:table-cell">Email</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm hidden sm:table-cell">Phone</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm">Invite Status</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm hidden md:table-cell">Referred By</th>
                      <th className="text-left font-semibold px-4 py-3 text-sm hidden sm:table-cell">Referral ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedContacts.length === 0 ? (
                      <TableEmptyState message="No contacts found" />
                    ) : (
                    paginatedContacts.map((c) => {
                      const ac = ACCEPT_CONFIG[c.accepted];
                      const AcIcon = ac.icon;
                      return (
                        <tr
                          key={c.id}
                          className="border-b border-border hover:bg-secondary/30 transition-colors"
                        >
                          <td className="font-medium px-4 py-3">{c.name}</td>
                          <td className="hidden md:table-cell text-sm text-muted-foreground px-4 py-3">
                            {c.email || '—'}
                          </td>
                          <td className="hidden sm:table-cell text-sm text-muted-foreground px-4 py-3">
                            {c.phone || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${ac.cls}`}
                            >
                              <AcIcon className="w-3 h-3" />
                              {ac.label}
                            </span>
                          </td>
                          <td className="hidden md:table-cell text-sm text-muted-foreground px-4 py-3">
                            {c.referrerName}
                          </td>
                          <td className="hidden sm:table-cell font-mono text-xs text-muted-foreground px-4 py-3">
                            {c.referrerId.slice(0, 8)}…
                          </td>
                        </tr>
                      );
                    })
                  )}
                  </tbody>
                </table>
              </div>
            )}

            {contactTotalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Showing {contactPage * CONTACT_PAGE_SIZE + 1}–
                  {Math.min(
                    (contactPage + 1) * CONTACT_PAGE_SIZE,
                    filteredContacts.length
                  )}{' '}
                  of {filteredContacts.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={contactPage === 0}
                    onClick={() => setContactPage((p) => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: contactTotalPages }).map((_, i) => (
                    <Button
                      key={i}
                      variant={contactPage === i ? 'default' : 'ghost'}
                      size="icon"
                      className="h-8 w-8 text-xs"
                      onClick={() => setContactPage(i)}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={contactPage >= contactTotalPages - 1}
                    onClick={() => setContactPage((p) => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'students' && (
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 p-5 border-b border-border">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, course, program…"
                  value={studentSearch}
                  onChange={(e) => { setStudentSearch(e.target.value); setStudentsPage(0); }}
                  className="pl-9"
                />
              </div>
              <select
                value={studentCourseFilter}
                onChange={(e) => { setStudentCourseFilter(e.target.value); setStudentsPage(0); }}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm min-w-[160px]"
              >
                <option value="">All courses</option>
                {uniqueCourses.map((c) => (
                  <option key={c.courseId} value={c.courseId}>{c.courseTitle}</option>
                ))}
              </select>
              <select
                value={studentProgramFilter}
                onChange={(e) => { setStudentProgramFilter(e.target.value); setStudentsPage(0); }}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm min-w-[160px]"
              >
                <option value="">All programs</option>
                {uniquePrograms.map((p) => (
                  <option key={p.slug} value={p.slug}>{p.title}</option>
                ))}
              </select>
            </div>
            {studentsLoading ? (
              <AdminTableSkeleton rows={8} cols={7} />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-secondary/50 border-b border-border">
                        <th className="text-left font-semibold px-4 py-3 text-sm">Student</th>
                        <th className="text-left font-semibold px-4 py-3 text-sm hidden md:table-cell">Email / Phone</th>
                        <th className="text-left font-semibold px-4 py-3 text-sm">Course</th>
                        <th className="text-left font-semibold px-4 py-3 text-sm hidden sm:table-cell">Program</th>
                        <th className="text-left font-semibold px-4 py-3 text-sm">Status</th>
                        <th className="text-left font-semibold px-4 py-3 text-sm">Enrolled</th>
                        <th className="text-right font-semibold px-4 py-3 text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEnrollments.length === 0 ? (
                        <TableEmptyState message="No students match the filters" />
                      ) : (
                        paginatedEnrollments.map((e) => {
                          const learnerEnrollments = enrollmentsByLearner.get(e.learnerId) ?? [];
                          const moreCount = learnerEnrollments.length - 1;
                          return (
                            <tr key={e.id} className="border-b border-border hover:bg-secondary/30 transition-colors">
                              <td className="font-medium px-4 py-3">
                                <span className={e.isNew ? 'font-semibold' : ''}>{e.learnerName}</span>
                                {e.isNew && <span className="ml-1.5 w-2 h-2 rounded-full bg-destructive inline-block" aria-hidden />}
                              </td>
                              <td className="hidden md:table-cell text-sm text-muted-foreground px-4 py-3">
                                {e.learnerEmail}
                                {e.learnerPhone ? ` · ${e.learnerPhone}` : ''}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {e.courseTitle}
                                {moreCount > 0 && <span className="text-muted-foreground"> +{moreCount}</span>}
                              </td>
                              <td className="hidden sm:table-cell text-sm text-muted-foreground px-4 py-3">{e.programTitle}</td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-africa-green/10 text-africa-green border-africa-green/20">
                                  {e.status}
                                </span>
                              </td>
                              <td className="text-sm text-muted-foreground px-4 py-3">{formatDate(e.enrolledAt)}</td>
                              <td className="text-right px-4 py-3">
                                <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => setSelectedEnrollment(e)}>
                                  <Eye className="w-4 h-4" />
                                  View
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {studentsTotalPages > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Showing {studentsPage * STUDENTS_PAGE_SIZE + 1}–
                      {Math.min((studentsPage + 1) * STUDENTS_PAGE_SIZE, filteredEnrollments.length)} of {filteredEnrollments.length}
                    </p>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" disabled={studentsPage === 0} onClick={() => setStudentsPage((p) => p - 1)}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      {Array.from({ length: studentsTotalPages }).map((_, i) => (
                        <Button key={i} variant={studentsPage === i ? 'default' : 'ghost'} size="icon" className="h-8 w-8 text-xs" onClick={() => setStudentsPage(i)}>
                          {i + 1}
                        </Button>
                      ))}
                      <Button variant="ghost" size="icon" className="h-8 w-8" disabled={studentsPage >= studentsTotalPages - 1} onClick={() => setStudentsPage((p) => p + 1)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'tutors' && (
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-border">
              <h2 className="text-lg font-display font-bold text-foreground">Tutors</h2>
              <Button type="button" className="bg-accent text-accent-foreground hover:bg-gold-light gap-2" onClick={() => { setTutorInviteOpen(true); setTutorInviteEmail(''); }}>
                <Plus className="w-4 h-4" /> Add tutor
              </Button>
            </div>
            {tutorsLoading ? (
              <div className="p-6">
                <div className="h-4 w-48 bg-muted rounded animate-pulse mb-4" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="rounded-xl border border-border p-5 flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-muted animate-pulse shrink-0" />
                      <div className="flex-1 space-y-2 min-w-0">
                        <div className="h-4 bg-muted rounded animate-pulse w-2/3" />
                        <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-6">
                {pendingInvites.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Pending invites</h3>
                    <div className="flex flex-wrap gap-2">
                      {pendingInvites.map((inv) => (
                        <div key={inv.id} className="inline-flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-africa-yellow/10 border border-africa-yellow/20 text-sm">
                          <Mail className="w-4 h-4 text-africa-yellow shrink-0" />
                          <span>{inv.email}</span>
                          {inv.phone && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="w-3.5 h-3.5" /> {inv.phone}
                            </span>
                          )}
                          {inv.courseTitle && (
                            <span className="text-xs font-medium text-foreground">→ {inv.courseTitle}</span>
                          )}
                          <span className="text-xs text-muted-foreground">Invited {formatDate(inv.createdAt)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                            aria-label={`Cancel invite for ${inv.email}`}
                            disabled={deletingInviteId === inv.id}
                            onClick={() => setInviteToCancel(inv)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Tutor profile cards</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tutors.length === 0 ? (
                    <div className="col-span-full flex flex-col items-center justify-center py-16 px-4 text-center">
                      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Users className="w-7 h-7 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground font-medium">No tutors yet. Use “Add tutor” to send an invite.</p>
                    </div>
                  ) : (
                    tutors.map((t) => (
                      <div key={t.id} className="rounded-xl border border-border bg-secondary/20 p-5 flex items-center gap-4">
                        {t.pictureUrl ? (
                          <img src={t.pictureUrl} alt="" className="w-14 h-14 rounded-full object-cover border border-border" />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-lg">
                            {(t.name || t.email).slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-foreground truncate">{t.name || t.email}</p>
                          <p className="text-sm text-muted-foreground truncate">{t.email}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            aria-label={`Remove ${t.name || t.email} as tutor`}
                            onClick={(e) => { e.stopPropagation(); setTutorToRemove(t); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            aria-label={`View or manage ${t.name || t.email}`}
                            onClick={() => setSelectedTutor(t)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enrollment (student) detail */}
        {selectedEnrollment && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            onClick={() => setSelectedEnrollment(null)}
          >
            <div
              className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-display font-bold text-foreground">Enrollment details</h2>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedEnrollment(null)}>Close</Button>
                </div>
                <div className="space-y-4">
                  <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Student</p>
                    <p className="font-medium text-foreground">{selectedEnrollment.learnerName}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{selectedEnrollment.learnerEmail}</span>
                      {selectedEnrollment.learnerPhone && (
                        <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{selectedEnrollment.learnerPhone}</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Course & program</p>
                    <p className="font-medium text-foreground">{selectedEnrollment.courseTitle}</p>
                    <p className="text-sm text-muted-foreground">{selectedEnrollment.programTitle}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-africa-green/10 text-africa-green border-africa-green/20">
                      {selectedEnrollment.status}
                    </span>
                    <span className="text-sm text-muted-foreground">Enrolled {formatDate(selectedEnrollment.enrolledAt)}</span>
                    {selectedEnrollment.isNew && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/15 text-destructive">New</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm cancel invite */}
        <ConfirmDialog
          open={!!inviteToCancel}
          onClose={() => setInviteToCancel(null)}
          title="Cancel invite?"
          description={inviteToCancel ? `Cancel the invite for ${inviteToCancel.email}? They will no longer be able to accept it.` : ''}
          confirmLabel="Cancel invite"
          variant="danger"
          loading={deletingInviteId === inviteToCancel?.id}
          onConfirm={async () => {
            if (!inviteToCancel) return;
            setDeletingInviteId(inviteToCancel.id);
            try {
              await deleteTutorInvite(inviteToCancel.id);
              toast.success('Invite cancelled.');
              const data = await getAdminTutors();
              setTutors(data.tutors);
              setPendingInvites(data.pendingInvites);
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Failed to cancel invite');
              throw err;
            } finally {
              setDeletingInviteId(null);
            }
          }}
        />

        {/* Confirm remove tutor */}
        <ConfirmDialog
          open={!!tutorToRemove}
          onClose={() => setTutorToRemove(null)}
          title="Remove tutor?"
          description={tutorToRemove ? `${tutorToRemove.name || tutorToRemove.email} will be changed to a student and lose access to manage courses and programs. They can still sign in and view content as a learner.` : ''}
          confirmLabel="Remove tutor"
          variant="danger"
          loading={tutorRemoving}
          onConfirm={async () => {
            if (!tutorToRemove) return;
            setTutorRemoving(true);
            try {
              await removeTutor(tutorToRemove.id);
              toast.success('Tutor removed. They now have student access only.');
              setTutorToRemove(null);
              if (selectedTutor?.id === tutorToRemove.id) setSelectedTutor(null);
              const data = await getAdminTutors();
              setTutors(data.tutors);
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Failed to remove tutor');
              throw err;
            } finally {
              setTutorRemoving(false);
            }
          }}
        />

        {/* Tutor view / manage modal */}
        {selectedTutor && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            onClick={() => { setSelectedTutor(null); setTutorEditAssignments(false); }}
          >
            <div
              className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-display font-bold text-foreground">Tutor details</h2>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedTutor(null); setTutorEditAssignments(false); }}>Close</Button>
                </div>
                <div className="flex items-center gap-4 mb-6">
                  {selectedTutor.pictureUrl ? (
                    <img src={selectedTutor.pictureUrl} alt="" className="w-16 h-16 rounded-full object-cover border border-border" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xl">
                      {(selectedTutor.name || selectedTutor.email).slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-foreground text-lg">{selectedTutor.name || selectedTutor.email}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> {selectedTutor.email}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Tutor since {formatDate(selectedTutor.createdAt)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Assign <strong>programs</strong> to let them manage all courses in that program, or assign individual <strong>courses</strong> for a single course. Click Edit to change.
                  </p>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assigned programs & courses</p>
                    {!tutorEditAssignments ? (
                      <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => { setTutorEditAssignments(true); setTutorAssignmentsDraft(selectedTutor.assignedCourses?.map((c) => c.courseId) ?? []); setTutorProgramAssignmentsDraft(selectedTutor.assignedPrograms?.map((p) => p.programId) ?? []); }}>
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button type="button" variant="ghost" size="sm" onClick={() => setTutorEditAssignments(false)}>Cancel</Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={tutorAssignmentsSaving}
                          onClick={async () => {
                            setTutorAssignmentsSaving(true);
                            try {
                              await updateTutorAssignments(selectedTutor.id, tutorAssignmentsDraft, tutorProgramAssignmentsDraft);
                              toast.success('Assignments updated. Changes will appear on the tutor’s dashboard.');
                              const data = await getAdminTutors();
                              setTutors(data.tutors);
                              const updated = data.tutors.find((t) => t.id === selectedTutor.id);
                              if (updated) setSelectedTutor(updated);
                              setTutorEditAssignments(false);
                            } catch (err) {
                              toast.error(err instanceof Error ? err.message : 'Failed to update assignments');
                            } finally {
                              setTutorAssignmentsSaving(false);
                            }
                          }}
                        >
                          {tutorAssignmentsSaving ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                    )}
                  </div>
                  {tutorEditAssignments ? (
                    <div className="space-y-4 max-h-64 overflow-y-auto rounded-lg border border-border p-3">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1.5">Programs (manage entire program)</p>
                        {programsListForAssign.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Loading programs…</p>
                        ) : (
                          <div className="space-y-1">
                            {programsListForAssign.map((p) => (
                              <label key={p.programId} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-secondary/50">
                                <input
                                  type="checkbox"
                                  checked={tutorProgramAssignmentsDraft.includes(p.programId)}
                                  onChange={(e) => {
                                    if (e.target.checked) setTutorProgramAssignmentsDraft((d) => [...d, p.programId]);
                                    else setTutorProgramAssignmentsDraft((d) => d.filter((id) => id !== p.programId));
                                  }}
                                  className="rounded border-border"
                                />
                                <span className="text-sm">{p.programTitle}</span>
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-blue/10 text-africa-blue border border-africa-blue/20">Program</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1.5">Courses (single course)</p>
                        {coursesForAssign.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Loading courses…</p>
                        ) : (
                          <div className="space-y-1">
                            {coursesForAssign.map((c) => (
                              <label key={c.courseId} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-secondary/50">
                                <input
                                  type="checkbox"
                                  checked={tutorAssignmentsDraft.includes(c.courseId)}
                                  onChange={(e) => {
                                    if (e.target.checked) setTutorAssignmentsDraft((d) => [...d, c.courseId]);
                                    else setTutorAssignmentsDraft((d) => d.filter((id) => id !== c.courseId));
                                  }}
                                  className="rounded border-border"
                                />
                                <span className="text-sm">{c.programTitle} → {c.courseTitle}</span>
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-green/10 text-africa-green border border-africa-green/20">Course</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (selectedTutor.assignedPrograms?.length ?? 0) === 0 && (selectedTutor.assignedCourses?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">No programs or courses assigned.</p>
                  ) : (
                    <ul className="space-y-2">
                      {selectedTutor.assignedPrograms?.map((p) => (
                        <li key={p.programId} className="flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                          <GraduationCap className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="font-medium text-foreground">{p.programTitle}</span>
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-blue/10 text-africa-blue border border-africa-blue/20">Program</span>
                        </li>
                      ))}
                      {selectedTutor.assignedCourses?.map((c) => (
                        <li key={c.courseId} className="flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                          <GraduationCap className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="font-medium text-foreground">{c.courseTitle}</span>
                          <span className="text-muted-foreground">· {c.programTitle}</span>
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-green/10 text-africa-green border border-africa-green/20">Course</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <SlideOver open={tutorInviteOpen} onClose={() => { setTutorInviteOpen(false); setTutorInviteEmail(''); setTutorInvitePhone(''); setTutorInviteCourseId(''); setTutorInviteFullAccess(true); setTutorInviteCourseSearch(''); setTutorInviteProgramFilter(''); setTutorInviteEmailError(''); }} title="Invite tutor">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Send an invite link to the tutor’s email. Assign a course and set permissions. They’ll sign in with Google and be scoped to that course.</p>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
            <Input
              type="email"
              placeholder="tutor@example.com"
              value={tutorInviteEmail}
              onChange={(e) => {
                setTutorInviteEmail(e.target.value);
                setTutorInviteEmailError('');
              }}
              onBlur={() => {
                const v = tutorInviteEmail.trim();
                if (!v) { setTutorInviteEmailError(''); return; }
                const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
                setTutorInviteEmailError(valid ? '' : 'Please enter a valid email address.');
              }}
              className={tutorInviteEmailError ? 'border-destructive' : ''}
            />
            {tutorInviteEmailError && <p className="text-xs text-destructive mt-1">{tutorInviteEmailError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Phone (optional)</label>
            <Input
              type="tel"
              placeholder="+254 700 000 000"
              value={tutorInvitePhone}
              onChange={(e) => setTutorInvitePhone(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Assign course</label>
            <p className="text-xs text-muted-foreground mb-2">Select a program to filter; then choose a course from the list below.</p>
            <div className="space-y-2">
              <label className="block text-xs font-medium text-muted-foreground">Program (filter)</label>
              <select
                value={tutorInviteProgramFilter}
                onChange={(e) => setTutorInviteProgramFilter(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">All programs</option>
                {programsForAssign.map((p) => (
                  <option key={p.programId} value={p.programId}>{p.programTitle}</option>
                ))}
              </select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search courses…"
                  value={tutorInviteCourseSearch}
                  onChange={(e) => setTutorInviteCourseSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <label className="block text-xs font-medium text-muted-foreground">Course</label>
              <select
                value={tutorInviteCourseId}
                onChange={(e) => setTutorInviteCourseId(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">No course (tutor with no assignment)</option>
                {(() => {
                  const selected = coursesForAssign.find((c) => c.courseId === tutorInviteCourseId);
                  const list = selected && !filteredCoursesForAssign.some((c) => c.courseId === tutorInviteCourseId)
                    ? [selected, ...filteredCoursesForAssign]
                    : filteredCoursesForAssign;
                  return list.map((c) => (
                    <option key={c.courseId} value={c.courseId}>
                      {c.programTitle} → {c.courseTitle}
                    </option>
                  ));
                })()}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="tutor-full-access"
              checked={tutorInviteFullAccess}
              onChange={(e) => setTutorInviteFullAccess(e.target.checked)}
              className="rounded border-border"
            />
            <label htmlFor="tutor-full-access" className="text-sm font-medium text-foreground">Full access (edit course content, sections, lessons)</label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setTutorInviteOpen(false)}>Cancel</Button>
            <Button
              className="bg-accent text-accent-foreground hover:bg-gold-light"
              disabled={!tutorInviteEmail.trim() || inviteSending || !!tutorInviteEmailError}
              onClick={async () => {
                const email = tutorInviteEmail.trim();
                const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                if (!validEmail) {
                  setTutorInviteEmailError('Please enter a valid email address.');
                  return;
                }
                setInviteSending(true);
                try {
                  const result = await inviteTutor(email, {
                    phone: tutorInvitePhone.trim() || undefined,
                    courseId: tutorInviteCourseId || undefined,
                    permissions: { fullAccess: tutorInviteFullAccess },
                  });
                  toast.success(result.sent ? 'Invite email sent.' : result.message);
                  setTutorInviteOpen(false);
                  setTutorInviteEmail('');
                  setTutorInvitePhone('');
                  setTutorInviteCourseId('');
                  setTutorInviteFullAccess(true);
                  if (activeTab === 'tutors') getAdminTutors().then((d) => { setTutors(d.tutors); setPendingInvites(d.pendingInvites); }).catch(() => {});
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to send invite');
                } finally {
                  setInviteSending(false);
                }
              }}
            >
              {inviteSending ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </div>
      </SlideOver>

      {/* Detail dialog */}
      {selectedReferral && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedReferral(null)}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-display font-bold text-foreground">
                  Referral details
                </h2>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedReferral(null)}
                >
                  Close
                </Button>
              </div>
              <p className="text-xs text-muted-foreground font-mono mb-4">
                {selectedReferral.id}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Submitted {formatDate(selectedReferral.createdAt)}
              </p>

              <div className="bg-secondary/50 rounded-lg p-4 space-y-2 mb-6">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Referrer
                </p>
                <p className="font-medium text-foreground">
                  {selectedReferral.referrerName}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {selectedReferral.referrerEmail}
                  </span>
                  {selectedReferral.referrerPhone && (
                    <span className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" />
                      {selectedReferral.referrerPhone}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Referred contacts ({selectedReferral.contacts.length})
              </p>
              <div className="space-y-2">
                {selectedReferral.contacts.map((c, i) => {
                  const ac = ACCEPT_CONFIG[c.accepted];
                  const AcIcon = ac.icon;
                  const borderColors = [
                    'border-l-africa-red',
                    'border-l-africa-orange',
                    'border-l-africa-yellow',
                    'border-l-africa-green',
                    'border-l-africa-blue',
                    'border-l-africa-purple',
                  ];
                  return (
                    <div
                      key={c.id}
                      className={`border-l-4 ${
                        borderColors[i % borderColors.length]
                      } bg-secondary/30 rounded-r-lg p-3`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">{c.name}</p>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${ac.cls}`}
                        >
                          <AcIcon className="w-2.5 h-2.5" />
                          {ac.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                        {c.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {c.email}
                          </span>
                        )}
                        {c.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {c.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
